import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const LINE1 = ["End-to-End"];
const LINE2 = ["Telecom"];
const LINE3 = ["Infrastructure"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.14 },
  }),
};

const TAGS = [
  "Civil Works", "Tower Deployment", "Fiber Networks", "OLT & GPON",
  "Microwave", "4G/5G Rollouts", "O&M", "Material Supply",
];

export default function Hero({ onScrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.25, 0.8]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden flex items-end"
      data-testid="hero-section"
    >
      {/* Parallax image */}
      <motion.div className="absolute inset-0 -z-10" style={{ y: imgY, scale: imgScale }}>
        <img
          src="https://images.pexels.com/photos/2352277/pexels-photo-2352277.jpeg"
          alt="Telecommunication tower"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 -z-10 bg-[#060A14]"
        style={{ opacity: overlayOpacity }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#060A14] via-transparent to-[#060A14]/70" />
      <div className="absolute inset-0 -z-10 blueprint-grid opacity-30" />

      <div className="relative max-w-[1400px] mx-auto w-full px-5 md:px-10 pb-16 md:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-2 w-2 bg-cyan animate-pulse" style={{ background: "#00F0FF" }} />
          <span className="mono-label" style={{ color: "#94A3B8" }}>
            A3NETWORKS (OPC) Private Limited · Est. 2025
          </span>
        </motion.div>

        <h1 data-testid="hero-headline" className="font-display font-black text-white tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-8xl">
          {[LINE1, LINE2, LINE3].map((line, i) => (
            <span key={i} className="reveal-line-wrap">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {i === 2 ? (
                  <span>
                    <span className="text-cyan" style={{ color: "#00F0FF" }}>Infra</span>structure
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg leading-relaxed"
          style={{ color: "#94A3B8" }}
        >
          A professionally managed telecom implementation and material supply
          partner — delivering optical access networks, tower deployment,
          wireless backhaul and end-to-end network rollouts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => onScrollTo("services")}
            data-testid="hero-explore-button"
            className="group flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-7 py-4 mono-label transition-colors duration-300"
          >
            Explore Services
            <ArrowUpRight size={16} className="cta-arrow" />
          </button>
          <button
            onClick={() => onScrollTo("contact")}
            data-testid="hero-contact-button"
            className="group flex items-center gap-2 border border-white/20 hover:border-cyan text-white px-7 py-4 mono-label transition-colors duration-300"
          >
            Get in Touch
            <ArrowUpRight size={16} className="cta-arrow" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-14 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-6"
        >
          {TAGS.map((t) => (
            <span key={t} className="mono-label" style={{ color: "#64748B" }}>
              {t}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-6 hidden md:flex items-center gap-2"
        style={{ color: "#64748B" }}
      >
        <span className="mono-label">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
